import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Download, Plus, AlertTriangle, Search,
  Filter, X, Pencil, Trash2, ExternalLink,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type { Agreement, Customer, User } from '@/types/api'
import { CONTRACT_TYPES } from '@/types/api'
import { Drawer } from '@/components/primitives/Drawer'

// ── Types ─────────────────────────────────────────────────────────────────────

type DrawerMode = 'view' | 'edit' | 'create' | null

interface FormData {
  customerId:      string
  contractType:    string
  title:           string
  referenceNumber: string
  signedDate:      string
  startDate:       string
  endDate:         string
  value:           string
  currency:        string
  mrr:             string
  ownerId:         string
  documentUrl:     string
  notes:           string
}

const EMPTY_FORM: FormData = {
  customerId: '', contractType: 'service', title: '', referenceNumber: '',
  signedDate: '', startDate: '', endDate: '', value: '', currency: 'SEK',
  mrr: '', ownerId: '', documentUrl: '', notes: '',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHIP_COLORS = [
  'oklch(0.6 0.15 30)',  'oklch(0.55 0.13 245)', 'oklch(0.6 0.14 200)',
  'oklch(0.55 0.16 25)', 'oklch(0.55 0.14 145)', 'oklch(0.5 0.16 260)',
  'oklch(0.55 0.13 60)', 'oklch(0.5 0.13 350)',  'oklch(0.6 0.12 300)',
  'oklch(0.55 0.12 120)',
]

function chipColor(id: number): string {
  return CHIP_COLORS[id % CHIP_COLORS.length]
}

function initials(name: string | null | undefined): string {
  return (name ?? '?').split(' ').filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase()
}

function formatMo(start: string | null, end: string | null): string | null {
  if (!start || !end) return null
  const months = (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  return months.toFixed(0) + ' mo'
}

function toFormData(a: Agreement): FormData {
  return {
    customerId:      String(a.customerId),
    contractType:    a.contractType,
    title:           a.title,
    referenceNumber: a.referenceNumber ?? '',
    signedDate:      a.signedDate  ?? '',
    startDate:       a.startDate   ?? '',
    endDate:         a.endDate     ?? '',
    value:           a.value  != null ? String(a.value)  : '',
    currency:        a.currency,
    mrr:             a.mrr    != null ? String(a.mrr)    : '',
    ownerId:         a.ownerId != null ? String(a.ownerId) : '',
    documentUrl:     a.documentUrl ?? '',
    notes:           a.notes       ?? '',
  }
}

const TYPE_LABELS: Record<string, string> = {
  service: 'Service Agreement', msa: 'MSA', sow: 'SOW',
  nda: 'NDA', dpa: 'DPA', framework: 'Framework', other: 'Other',
}

const STATUS_CFG: Record<string, { label: string; cls: string; dotCls: string }> = {
  'active':      { label: 'Active',      cls: 'badge-ok',   dotCls: 'dot-ok'   },
  'renewal-due': { label: 'Renewal due', cls: 'badge-warn', dotCls: 'dot-warn' },
  'expired':     { label: 'Expired',     cls: 'badge-err',  dotCls: 'dot-err'  },
  'draft':       { label: 'Draft',       cls: 'badge-muted',dotCls: 'dot-muted'},
}

const STATUS_CHIPS = [
  { value: 'all',         label: 'All'         },
  { value: 'active',      label: 'Active'      },
  { value: 'renewal-due', label: 'Renewal due' },
  { value: 'expired',     label: 'Expired'     },
]

// ── Sub-components ────────────────────────────────────────────────────────────

function CustChip({ id, name }: { id: number; name: string | null }) {
  const n = name || '?'
  return (
    <span className="row" style={{ gap: 7 }}>
      <span style={{
        width: 18, height: 18, borderRadius: 4, background: chipColor(id), color: 'white',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9.5, fontWeight: 700,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        letterSpacing: '-0.04em', flexShrink: 0,
      }}>
        {initials(n)}
      </span>
      <span style={{ fontWeight: 500 }}>{n}</span>
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG['active']
  return (
    <span className={`badge ${cfg.cls}`}>
      <span className={`dot ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4, color: 'var(--ns-text-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5 }}>{value}</div>
    </div>
  )
}

function FormLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ns-text-muted)', marginBottom: 4 }}>
      {children}
    </label>
  )
}

// ── ContractDetail ────────────────────────────────────────────────────────────

interface DetailProps {
  agreement: Agreement
  onClose:   () => void
  onEdit:    () => void
  onDelete:  () => void
  deleting:  boolean
}

function ContractDetail({ agreement: a, onClose, onEdit, onDelete, deleting }: DetailProps) {
  const term = formatMo(a.startDate, a.endDate)
  return (
    <>
      <div className="drawer-head">
        <div>
          <div className="row" style={{ marginBottom: 6, gap: 8 }}>
            <StatusBadge status={a.status} />
            <span className="mono muted" style={{ fontSize: 12 }}>{a.referenceNumber ?? `#${a.id}`}</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>{a.title}</div>
          <div style={{ marginTop: 8 }}><CustChip id={a.customerId} name={a.customerName} /></div>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={16} /></button>
      </div>

      <div className="drawer-body">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <KV label="Total value" value={
            a.value != null
              ? <span className="mono">{a.value.toLocaleString('sv-SE')} {a.currency}</span>
              : '—'
          } />
          <KV label="MRR" value={
            a.mrr != null
              ? <span className="mono">{a.mrr.toLocaleString('sv-SE')} {a.currency}</span>
              : '—'
          } />
          <KV label="Type"  value={TYPE_LABELS[a.contractType] ?? a.contractType} />
          <KV label="Owner" value={
            a.ownerName
              ? <span className="row" style={{ gap: 7 }}><div className="avatar sm">{initials(a.ownerName)}</div>{a.ownerName}</span>
              : '—'
          } />
          {term && <KV label="Term" value={term} />}
          <KV label="Start"  value={a.startDate  ? <span className="mono">{a.startDate}</span>  : '—'} />
          <KV label="End"    value={a.endDate    ? <span className="mono">{a.endDate}</span>    : '—'} />
          {a.signedDate && <KV label="Signed" value={<span className="mono">{a.signedDate}</span>} />}
        </div>

        {a.documentUrl && (
          <a
            href={a.documentUrl} target="_blank" rel="noreferrer"
            className="btn btn-ghost btn-sm"
            style={{ marginBottom: 16, display: 'inline-flex' }}
          >
            <ExternalLink size={13} />View document
          </a>
        )}

        {a.notes && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>Notes</div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{a.notes}</div>
          </div>
        )}
      </div>

      <div className="drawer-foot">
        <button
          className="btn btn-ghost"
          style={{ color: 'var(--ns-err)' }}
          onClick={onDelete}
          disabled={deleting}
        >
          <Trash2 size={13} />{deleting ? 'Deleting…' : 'Delete'}
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={onEdit}><Pencil size={13} />Edit</button>
      </div>
    </>
  )
}

// ── ContractForm ──────────────────────────────────────────────────────────────

interface FormProps {
  form:      FormData
  setForm:   (f: FormData) => void
  customers: Customer[]
  users:     User[]
  saving:    boolean
  mode:      'create' | 'edit'
  onSubmit:  () => void
  onCancel:  () => void
}

function ContractForm({ form, setForm, customers, users, saving, mode, onSubmit, onCancel }: FormProps) {
  function field(key: keyof FormData) {
    return {
      value:    form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setForm({ ...form, [key]: e.target.value }),
    }
  }

  const canSubmit = !!form.customerId && !!form.title.trim() && !saving

  return (
    <>
      <div className="drawer-head">
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          {mode === 'create' ? 'New contract' : 'Edit contract'}
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onCancel}><X size={16} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <FormLabel>Customer *</FormLabel>
          <select className="input" {...field('customerId')}>
            <option value="">Select customer…</option>
            {customers.filter(c => c.isActive).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <FormLabel>Contract type *</FormLabel>
          <select className="input" {...field('contractType')}>
            {CONTRACT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <FormLabel>Title *</FormLabel>
          <input className="input" placeholder="e.g. Analytics Platform Build-out" {...field('title')} />
        </div>

        <div>
          <FormLabel>Reference number</FormLabel>
          <input className="input" placeholder="Auto-generated if blank" {...field('referenceNumber')} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <FormLabel>Start date</FormLabel>
            <input type="date" className="input" {...field('startDate')} />
          </div>
          <div>
            <FormLabel>End date</FormLabel>
            <input type="date" className="input" {...field('endDate')} />
          </div>
        </div>

        <div>
          <FormLabel>Signed date</FormLabel>
          <input type="date" className="input" {...field('signedDate')} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
          <div>
            <FormLabel>Total value</FormLabel>
            <input type="number" className="input" placeholder="0" min="0" {...field('value')} />
          </div>
          <div>
            <FormLabel>Currency</FormLabel>
            <input className="input" {...field('currency')} />
          </div>
        </div>

        <div>
          <FormLabel>MRR</FormLabel>
          <input type="number" className="input" placeholder="0" min="0" {...field('mrr')} />
        </div>

        <div>
          <FormLabel>Owner</FormLabel>
          <select className="input" {...field('ownerId')}>
            <option value="">Unassigned</option>
            {users.filter(u => u.isActive).map(u => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </select>
        </div>

        <div>
          <FormLabel>Document URL</FormLabel>
          <input type="url" className="input" placeholder="https://…" {...field('documentUrl')} />
        </div>

        <div>
          <FormLabel>Notes</FormLabel>
          <textarea
            className="input" rows={3}
            style={{ resize: 'vertical' }}
            {...field('notes')}
          />
        </div>
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={onSubmit} disabled={!canSubmit}>
          {saving ? 'Saving…' : mode === 'create' ? 'Create contract' : 'Save changes'}
        </button>
      </div>
    </>
  )
}

// ── ContractsPage ─────────────────────────────────────────────────────────────

export default function ContractsPage() {
  const qc = useQueryClient()

  const [drawerMode,    setDrawerMode]    = useState<DrawerMode>(null)
  const [selectedId,    setSelectedId]    = useState<number | null>(null)
  const [statusFilter,  setStatusFilter]  = useState('all')
  const [search,        setSearch]        = useState('')
  const [sortByRenewal, setSortByRenewal] = useState(false)
  const [form,          setForm]          = useState<FormData>(EMPTY_FORM)
  const [saving,        setSaving]        = useState(false)
  const [deleting,      setDeleting]      = useState(false)

  const { data: agreements = [], isLoading } = useQuery<Agreement[]>({
    queryKey: ['contracts'],
    queryFn:  () => api.get('/contracts/agreements'),
  })

  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers'],
    queryFn:  () => api.get('/customers'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn:  () => api.get('/users'),
  })

  const selected = agreements.find(a => a.id === selectedId) ?? null

  // ── Stats ────────────────────────────────────────────────────────────────────

  const totalValue  = agreements.reduce((s, a) => s + (a.value ?? 0), 0)
  const activeMRR   = agreements.filter(a => a.status === 'active').reduce((s, a) => s + (a.mrr ?? 0), 0)
  const renewing90  = agreements.filter(a => a.renewalDays != null && a.renewalDays >= 0 && a.renewalDays <= 90)
  const lengths     = agreements
    .filter((a): a is typeof a & { startDate: string; endDate: string } => !!a.startDate && !!a.endDate)
    .map(a => (new Date(a.endDate).getTime() - new Date(a.startDate).getTime()) / (1000 * 60 * 60 * 24 * 30.44))
  const avgLength   = lengths.length > 0
    ? (lengths.reduce((s, v) => s + v, 0) / lengths.length).toFixed(1)
    : null

  // ── Filtered list ────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = agreements
    if (statusFilter !== 'all') list = list.filter(a => a.status === statusFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(a =>
        a.title.toLowerCase().includes(q) ||
        (a.referenceNumber ?? '').toLowerCase().includes(q) ||
        (a.customerName ?? '').toLowerCase().includes(q),
      )
    }
    if (sortByRenewal) {
      list = [...list].sort((a, b) => {
        const da = a.renewalDays ?? Infinity
        const db = b.renewalDays ?? Infinity
        return da - db
      })
    }
    return list
  }, [agreements, statusFilter, search, sortByRenewal])

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function openCreate() { setForm(EMPTY_FORM); setDrawerMode('create') }
  function openView(id: number) { setSelectedId(id); setDrawerMode('view') }
  function openEdit() { if (selected) { setForm(toFormData(selected)); setDrawerMode('edit') } }
  function closeDrawer() { setDrawerMode(null); setSelectedId(null) }

  async function handleSubmit() {
    setSaving(true)
    const payload = {
      customerId:      Number(form.customerId),
      contractType:    form.contractType,
      title:           form.title.trim(),
      referenceNumber: form.referenceNumber.trim() || null,
      signedDate:      form.signedDate  || null,
      startDate:       form.startDate   || null,
      endDate:         form.endDate     || null,
      value:           form.value       ? Number(form.value)    : null,
      currency:        form.currency    || 'SEK',
      mrr:             form.mrr         ? Number(form.mrr)      : null,
      ownerId:         form.ownerId     ? Number(form.ownerId)  : null,
      documentUrl:     form.documentUrl || null,
      notes:           form.notes.trim() || null,
    }
    try {
      if (drawerMode === 'create') {
        const created = await api.post<Agreement>('/contracts/agreements', payload)
        await qc.invalidateQueries({ queryKey: ['contracts'] })
        setSelectedId(created.id)
        setDrawerMode('view')
      } else {
        await api.put(`/contracts/agreements/${selectedId}`, payload)
        await qc.invalidateQueries({ queryKey: ['contracts'] })
        setDrawerMode('view')
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!selectedId) return
    setDeleting(true)
    try {
      await api.delete(`/contracts/agreements/${selectedId}`)
      await qc.invalidateQueries({ queryKey: ['contracts'] })
      closeDrawer()
    } finally {
      setDeleting(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Contracts</div>
          <h1 className="page-title">Customer agreements</h1>
          <div className="page-subtitle">
            {agreements.length} {agreements.length === 1 ? 'contract' : 'contracts'} · {agreements.filter(a => a.status === 'active').length} active
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn"><Download size={14} />Export</button>
          <button className="btn btn-brand" onClick={openCreate}><Plus size={14} />New contract</button>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="stat-label">Total contract value</div>
          <div className="stat-value tabular">{(totalValue / 1_000_000).toFixed(1)}M</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>SEK · all-time</div>
        </div>
        <div className="stat">
          <div className="stat-label">Active MRR</div>
          <div className="stat-value tabular">{(activeMRR / 1_000).toFixed(0)}k</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>SEK / month</div>
        </div>
        <div className="stat">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {renewing90.length > 0 && <AlertTriangle size={12} style={{ color: 'var(--ns-warn)' }} />}
            Renewing in 90 days
          </div>
          <div className="stat-value tabular">{renewing90.length}</div>
          {renewing90.length > 0 && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              {renewing90.slice(0, 2).map(a => a.customerName).filter(Boolean).join(' · ')}
              {renewing90.length > 2 ? ` +${renewing90.length - 2}` : ''}
            </div>
          )}
        </div>
        <div className="stat">
          <div className="stat-label">Avg contract length</div>
          <div className="stat-value tabular">
            {avgLength ?? '—'}
            {avgLength && (
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ns-text-muted)', marginLeft: 4 }}>mo</span>
            )}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box" style={{ maxWidth: 280, flex: 'none', marginLeft: 0 }}>
          <Search size={14} />
          <input
            className="input" placeholder="Search contracts…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, paddingRight: 10 }}
          />
        </div>
        {STATUS_CHIPS.map(s => (
          <button
            key={s.value}
            className={`chip${statusFilter === s.value ? ' active' : ''}`}
            onClick={() => setStatusFilter(s.value)}
          >
            {s.label}
          </button>
        ))}
        <div className="spacer" />
        <button
          className={`btn btn-ghost btn-sm${sortByRenewal ? ' active' : ''}`}
          onClick={() => setSortByRenewal(v => !v)}
        >
          <Filter size={13} />{sortByRenewal ? 'Sorted by renewal' : 'Sort by renewal'}
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Reference</th>
              <th>Title</th>
              <th>Customer</th>
              <th style={{ width: 110 }}>Type</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 160, textAlign: 'right' }}>Value</th>
              <th style={{ width: 140 }}>Term</th>
              <th style={{ width: 110 }}>Renews</th>
              <th style={{ width: 60 }}>Owner</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 32 }}>Loading…</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  {agreements.length === 0 ? 'No contracts yet — create one to get started' : 'No contracts match the current filter'}
                </td>
              </tr>
            ) : filtered.map(a => (
              <tr key={a.id} className="row-clickable" onClick={() => openView(a.id)}>
                <td className="mono" style={{ fontSize: 12 }}>{a.referenceNumber ?? `#${a.id}`}</td>
                <td><div style={{ fontWeight: 500 }}>{a.title}</div></td>
                <td><CustChip id={a.customerId} name={a.customerName} /></td>
                <td className="muted" style={{ fontSize: 12.5 }}>{TYPE_LABELS[a.contractType] ?? a.contractType}</td>
                <td><StatusBadge status={a.status} /></td>
                <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 500 }}>
                  {a.value != null ? `${a.value.toLocaleString('sv-SE')} ${a.currency}` : '—'}
                </td>
                <td className="muted" style={{ fontSize: 12.5 }}>
                  {a.startDate && a.endDate
                    ? `${a.startDate} → ${a.endDate}`
                    : (a.startDate ?? '—')}
                </td>
                <td>
                  {a.renewalDays != null ? (
                    a.renewalDays >= 0 ? (
                      <span className="mono tabular" style={{ fontSize: 12.5, color: a.renewalDays <= 60 ? 'var(--ns-warn)' : 'var(--ns-text-muted)' }}>
                        in {a.renewalDays}d
                      </span>
                    ) : (
                      <span className="mono tabular" style={{ fontSize: 12.5, color: 'var(--ns-err)' }}>
                        {Math.abs(a.renewalDays)}d ago
                      </span>
                    )
                  ) : <span className="muted">—</span>}
                </td>
                <td>
                  {a.ownerName
                    ? <div className="avatar sm" title={a.ownerName}>{initials(a.ownerName)}</div>
                    : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Drawer open={drawerMode !== null} onClose={closeDrawer}>
        {drawerMode === 'view' && selected && (
          <ContractDetail
            agreement={selected} onClose={closeDrawer}
            onEdit={openEdit} onDelete={handleDelete} deleting={deleting}
          />
        )}
        {(drawerMode === 'edit' || drawerMode === 'create') && (
          <ContractForm
            form={form} setForm={setForm}
            customers={customers} users={users}
            saving={saving}
            mode={drawerMode === 'edit' ? 'edit' : 'create'}
            onSubmit={handleSubmit}
            onCancel={drawerMode === 'edit' ? () => setDrawerMode('view') : closeDrawer}
          />
        )}
      </Drawer>
    </div>
  )
}
